// src/components/distributionDashboard/QuotationDrawer.js

import React, { useEffect, useState } from "react";
import {
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Checkbox,
} from "@mui/material";
import { Loader2, Search, X, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom"; // or next/router

const QuotationDrawer = ({
  drawerOpen,
  handleCloseDrawer,
  proposalNumber,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  sampleIssueData,
}) => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  console.log("Sample Issue Data in QuotationDrawer:", sampleIssueData);
  const navigate = useNavigate();

  useEffect(() => {
    if (drawerOpen && proposalNumber) {
      fetchQuotations(proposalNumber);
      setSelectedItems([]); // clear previous selections
    }
  }, [drawerOpen, proposalNumber]);

  const fetchQuotations = async (propNo) => {
    setLoading(true);
    try {
      const apiUrl = `${BASE_URL}/api/CRMTaskMainListFilter/${COMPANY_CODE}/SAMPLEISSUELIST/-/-/-/${propNo}/${DEPARTMENT}/1900-01-01/1900-01-01/-/-/-`;
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error("Failed to fetch quotations");
      }

      const data = await response.json();
      const quots = Array.isArray(data) ? data : [data];
      setQuotations(quots);
    } catch (error) {
      console.error("Error fetching quotations:", error);
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuotations = quotations.filter((item) => {
    const s = searchTerm.toLowerCase();
    return (
      item.Code?.toLowerCase().includes(s) ||
      item.Description?.toLowerCase().includes(s) ||
      item.Status?.toLowerCase().includes(s) ||
      item.Sample_Qty?.toString().includes(s)
    );
  });

  const toggleItemSelection = (item) => {
    setSelectedItems((prev) => {
      const exists = prev.find((x) => x.quot_slNo === item.quot_slNo);
      if (exists) {
        return prev.filter((x) => x.quot_slNo !== item.quot_slNo);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleIssueSample = () => {
    const transferData = {
      proposalNumber,
      selectedItems,
      sampleIssueData: {
        proposalNo: sampleIssueData?.Proposal_No,
        customer: sampleIssueData?.Customer,
        cust_acc: sampleIssueData?.Cust_acc,
        salesperson: sampleIssueData?.Salesperson,
        salespersonAcc: sampleIssueData?.Salesperson_Acc,
        salesPersonAccDesc: sampleIssueData?.Account_Desc,
        salespersonId: sampleIssueData?.SalesPersonID,
        date: sampleIssueData?.Date,
        totalQty: sampleIssueData?.Total_Sample_Qty,
        cordinatorId: sampleIssueData?.CoordinatorID,
        cordinatorName: sampleIssueData?.Coordinator_Name,
      },
    };
    console.log("Cordinator", sampleIssueData?.CoordinatorID);

    navigate("/sample_issue", {
      state: transferData,
    });
  };
  const toggleSelectAll = () => {
    if (selectedItems.length === filteredQuotations.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredQuotations);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={handleCloseDrawer}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 700 } },
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 border-b bg-gradient-to-r from-purple-600 to-purple-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">
                Quotation Items <span> # {proposalNumber}</span>
              </h3>
              <p className="text-sm opacity-80">
                {sampleIssueData?.Customer} • {sampleIssueData?.Cust_acc}
              </p>
            </div>

            <IconButton onClick={handleCloseDrawer} sx={{ color: "white" }}>
              <X className="w-5 h-5" />
            </IconButton>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b bg-gray-50">
          <TextField
            fullWidth
            size="small"
            placeholder="Search item code, description, qty..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className="w-4 h-4 text-gray-400" />
                </InputAdornment>
              ),
            }}
          />
        </div>

        {/* Select All Bar - Fixed position above scrollable content */}
        <div className="flex-shrink-0 px-4 py-3 border-b bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={
                  filteredQuotations.length > 0 &&
                  selectedItems.length === filteredQuotations.length
                }
                indeterminate={
                  selectedItems.length > 0 &&
                  selectedItems.length < filteredQuotations.length
                }
                onChange={toggleSelectAll}
              />
              <span className="text-sm font-semibold text-gray-700">
                Select All
              </span>
            </div>
            <button
              disabled={selectedItems?.length === 0}
              onClick={handleIssueSample}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                selectedItems?.length === 0
                  ? "bg-blue-200 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              title={
                selectedItems.length === 0
                  ? `${selectedItems?.length} items selected`
                  : "No items selected"
              }
            >
              Issue Sample ({selectedItems.length})
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600 mr-2" />
              <span className="text-sm text-gray-600">Loading items...</span>
            </div>
          ) : filteredQuotations.length > 0 ? (
            <div className="space-y-2">
              {/* Header */}
              <div className="grid grid-cols-[40px_1fr_2fr_80px_100px] bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 rounded-md border border-gray-100">
                <span></span>
                <span>Code</span>
                <span>Description</span>
                <span className="text-center">Qty</span>
                <span className="text-right">Status</span>
              </div>

              {/* Rows */}
              {filteredQuotations.map((item, index) => (
                <div
                  key={`item-${index}`}
                  className="grid grid-cols-[40px_1fr_2fr_80px_100px] px-3 py-2 text-xs items-center border border-gray-100 rounded-md hover:shadow-sm transition-shadow bg-white"
                >
                  {/* Checkbox */}
                  <Checkbox
                    checked={
                      !!selectedItems.find(
                        (x) => x.quot_slNo === item.quot_slNo
                      )
                    }
                    onChange={() => toggleItemSelection(item)}
                    size="small"
                  />

                  {/* Code */}
                  <span className="font-medium text-gray-800">{item.Code}</span>

                  {/* Description */}
                  <span className="text-gray-700 break-words">
                    {item.Description}
                  </span>

                  {/* Qty */}
                  <span className="text-center font-semibold text-gray-800">
                    {item.Sample_Qty}
                  </span>

                  {/* Status */}
                  <span className="text-right text-gray-600">
                    {item.Status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <DollarSign className="w-12 h-12 mb-3 opacity-40" />
              <p className="text-sm">No items found</p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default QuotationDrawer;
